import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { IconTruck } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { SearchBar } from "../../../../components/common/input";
import { Select } from "../../../../components/forms/select";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { StatusChip } from "../../../../components/common/chip";
import { SimpleTable, type SimpleTableColumn } from "../../../../components/common/simple-table";
import { EmptyState } from "../../../../components/common/empty-state";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { SalesQueryDto, PurchaseOrderDto } from "../../../../api";
import { SalesQueryDtoSortOrderEnum } from "../../../../api";
import { usePurchaseOrders } from "../../hooks/usePurchaseOrders";
import { usePurchaseOrderStats } from "../../hooks/usePurchaseOrderStats";
import { mapPoStatus } from "../../utils/po-status";

type FilterTab = "all" | "orderPlaced" | "inTransit" | "delivered";

const FILTER_TABS: { value: FilterTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "orderPlaced", label: "Order Placed" },
  { value: "inTransit", label: "In Transit" },
  { value: "delivered", label: "Delivered" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "amount_desc", label: "Amount: High to Low" },
  { value: "amount_asc", label: "Amount: Low to High" },
];

const VALID_TABS = new Set<FilterTab>(FILTER_TABS.map((t) => t.value));
const PAGE_SIZE = 10;

type PoState = SalesQueryDto["state"];
const STATE_CONFIRMED = "confirmed" as unknown as PoState;

function statusMatchesTab(tab: FilterTab, status: ReturnType<typeof mapPoStatus>): boolean {
  if (tab === "all") return true;
  if (tab === "orderPlaced") return status === "processing";
  if (tab === "inTransit") return status === "inTransit";
  return status === "delivered";
}

function tabToQuery(tab: FilterTab): Partial<SalesQueryDto> & { inDelivery?: boolean } {
  if (tab === "inTransit") return { state: STATE_CONFIRMED, inDelivery: true };
  return { state: STATE_CONFIRMED };
}

function sortToQuery(sort: string): Pick<SalesQueryDto, "sortBy" | "sortOrder"> {
  if (sort === "oldest") return { sortBy: "date_order", sortOrder: SalesQueryDtoSortOrderEnum.Asc };
  if (sort === "amount_desc") return { sortBy: "amount_total", sortOrder: SalesQueryDtoSortOrderEnum.Desc };
  if (sort === "amount_asc") return { sortBy: "amount_total", sortOrder: SalesQueryDtoSortOrderEnum.Asc };
  return { sortBy: "date_order", sortOrder: SalesQueryDtoSortOrderEnum.Desc };
}


function formatLongDate(dateStr?: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function buildColumns(): SimpleTableColumn<PurchaseOrderDto>[] {
  return [
    {
      key: "name",
      label: "Purchase Order",
      className: "w-[30%] min-w-0",
      render: (row) => <Text variant="body" tone="dark">{row.name ?? `PO-${row.id}`}</Text>,
    },
    {
      key: "amount",
      label: "Amount (QAR)",
      className: "w-[20%]",
      render: (row) => (
        <Text variant="bodyBold" tone="dark">
          {row.amountTotal != null
            ? row.amountTotal.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
            : "—"}
        </Text>
      ),
    },
    {
      key: "date",
      label: "Order Date",
      className: "w-[20%]",
      render: (row) => <Text variant="body" tone="body">{formatLongDate(row.dateOrder)}</Text>,
    },
    {
      key: "status",
      label: "Status",
      className: "w-[15%]",
      render: (row) => <StatusChip status={mapPoStatus(row)} />,
    },
    {
      key: "action",
      label: "",
      className: "w-[15%] flex justify-end",
      render: () => (
        <Text variant="bodySmMedium" tone="accent" inline className="cursor-pointer">View</Text>
      ),
    },
  ];
}

export function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAdminContext = pathname.startsWith("/admin");
  const detailRoute = isAdminContext ? AppRoutes.ADMIN_PURCHASE_ORDER_DETAILS : AppRoutes.PURCHASE_ORDER_DETAILS;
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab") as FilterTab | null;
  const tab: FilterTab = tabParam && VALID_TABS.has(tabParam) ? tabParam : "all";
  const search = searchParams.get("search") ?? "";
  const sort = searchParams.get("sort") ?? "newest";
  const page = Number(searchParams.get("page") ?? "1") || 1;

  const setTab = (next: FilterTab) => {
    const p = new URLSearchParams(searchParams);
    next === "all" ? p.delete("tab") : p.set("tab", next);
    p.delete("page");
    setSearchParams(p);
  };

  const { data, isLoading } = usePurchaseOrders({
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
    ...tabToQuery(tab),
    ...sortToQuery(sort),
  } as unknown as SalesQueryDto);

  const { data: stats, isLoading: statsLoading } = usePurchaseOrderStats();

  const orders = (data?.data ?? []).filter((row) => statusMatchesTab(tab, mapPoStatus(row)));
  const pageCount = data?.meta?.pageCount ?? 1;
  const columns = buildColumns();

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      <Heading variant="h2" tone="dark">Purchase Orders</Heading>

      <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1 max-md:gap-3">
        <AccentStatCard
          accent="green"
          label="Total Purchase Orders"
          value={(stats?.totalPurchaseOrders ?? 0).toLocaleString()}
        />
        <AccentStatCard
          accent="cyan"
          label="In Transit"
          value={(stats?.inTransitPurchaseOrders ?? 0).toLocaleString()}
        />
        <AccentStatCard
          accent="amber"
          label="Delivered"
          value={(stats?.deliveredPurchaseOrders ?? 0).toLocaleString()}
        />
      </div>

      <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-3">
        <SegmentedControl
          options={FILTER_TABS}
          value={tab}
          onChange={setTab}
          aria-label="Filter purchase orders by status"
          className="max-md:self-start"
        />

        <div className="flex-1">
          <SearchBar
            value={search}
            onChange={(e) => {
              const p = new URLSearchParams(searchParams);
              e.target.value.trim() ? p.set("search", e.target.value) : p.delete("search");
              p.delete("page");
              setSearchParams(p);
            }}
            placeholder="Search"
          />
        </div>

        <Select
          name="sort"
          value={sort}
          onChange={(e) => {
            const p = new URLSearchParams(searchParams);
            e.target.value === "newest" ? p.delete("sort") : p.set("sort", e.target.value);
            p.delete("page");
            setSearchParams(p);
          }}
          options={SORT_OPTIONS}
          density="default"
          fullWidth={false}
        />
      </div>

      {isLoading || statsLoading ? (
        <ShimmerLoader message="Fetching purchase orders…">
          <div className="flex flex-col gap-2">
            {Array.from({ length: 7 }).map((_, i) => (
              <Shimmer key={i} className="h-14 w-full" />
            ))}
          </div>
        </ShimmerLoader>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite">
          <EmptyState
            icon={IconTruck}
            title={
              tab === "all" && !search.trim()
                ? "No purchase orders yet"
                : "No purchase orders match your filters"
            }
            description={
              tab === "all" && !search.trim()
                ? "Purchase orders will appear here once they are created."
                : "Try clearing the search or switching to the All tab."
            }
          />
        </div>
      ) : (
        <SimpleTable
          columns={columns}
          data={orders}
          keyExtractor={(o) => String(o.id)}
          onRowClick={(o) => navigate(detailRoute.replace(":id", String(o.id)))}
          footer={
            pageCount > 1 ? (
              <div className="flex justify-center">
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={(next) => {
                    const p = new URLSearchParams(searchParams);
                    p.set("page", String(next));
                    setSearchParams(p, { replace: true });
                  }}
                />
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}

export default PurchaseOrdersPage;
